import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useMemo, useRef, useState } from "react";

import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import { escapeRegExp } from "@storyboard/dispatch-view/query";
import type { DispatchField } from "@storyboard/dispatch-view/view";
import { fieldRefOf } from "@storyboard/dispatch-view/view";
import type { FieldRef, FilterNode, RadiusSpec } from "@storyboard/driver-query/predicate";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import { blankChosen, chosenValues, conditionsOnField, valuesCondition } from "@/hooks/dispatch/adhocConditions";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";
import { dispatchCoreKey } from "@/hooks/dispatch/projectDispatchRecord";
import { blankCount, valueOptions } from "@/hooks/dispatch/valueOptions";
import { useLocationFilterForm } from "@/hooks/driver/useLocationFilterForm";
import type { LocationFilter } from "@/types/driver";
import SegmentedControl from "@/ui/SegmentedControl";
import { zinc } from "@/ui/tokens";
import { denseSelectSx } from "./denseSelect";
import { EditorActions, editorInputSx, ValueChecklist } from "./ValueChecklist";

/** The column a header click opened the menu on (its id is the field it shows), and the header cell to anchor it to. */
export type ColumnMenuTarget = {
  columnId: DispatchField;
  label: string;
  kind: FieldKind;
  anchorEl: HTMLElement;
};

/** How the header editor narrows a column: by its values, a contained term, a span, a number, a radius, or not at all. */
type EditorKind = "values" | "contains" | "time" | "number" | "geo" | "none";

/** The core field whose text the Location column shows; its radius conditions target the coordinates instead. */
const COORDINATES_FIELD = dispatchCoreKey("coordinates");
const LOCATION_FIELD = dispatchCoreKey("location");

/**
 * The field a column's conditions target and the editor that writes them. The Location column filters by radius on
 * the coordinates; the channel alias has no editor, since the team picker decides which channel every row reads; the
 * message columns offer only a contained term, their values never repeating across rows.
 */
export function editorFor(field: DispatchField, kind: FieldKind): { field: DispatchField; editor: EditorKind } {
  if (field === LOCATION_FIELD || field === COORDINATES_FIELD) return { field: COORDINATES_FIELD, editor: "geo" };
  if (field === dispatchCoreKey("alias")) return { field, editor: "none" };
  if (field === dispatchCoreKey("recent_message") || field === dispatchCoreKey("item_text")) {
    return { field, editor: "contains" };
  }
  switch (kind.valueType) {
    case "timestamp":
      return { field, editor: "time" };
    case "number":
      return { field, editor: "number" };
    case "coordinates":
      return { field: COORDINATES_FIELD, editor: "geo" };
    default:
      return { field, editor: "values" };
  }
}

/** The column whose header carries a condition's mark: the Location column for a condition on the coordinates. */
export function columnIdForConditionField(field: DispatchField): string {
  return field === COORDINATES_FIELD ? LOCATION_FIELD : field;
}

/** The first positive leaf about the field among the conditions in force, so the editor opens on what applies. */
function positiveLeaf(conditions: readonly FilterNode[]): Extract<FilterNode, { field: FieldRef }> | null {
  for (const condition of conditions) {
    if ("field" in condition) return condition;
  }
  return null;
}

/** Undoes {@link escapeRegExp} so a contained term reads back the way it was typed. */
function unescapeRegExp(pattern: string): string {
  return pattern.replace(/\\([.*+?^${}()|[\]\\])/g, "$1");
}

const selectSx = denseSelectSx(30);

const menuSlotProps = {
  paper: { sx: { bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}` } },
} as const;

const sectionLabelSx = {
  px: 0.5,
  pt: 0.75,
  pb: 0.25,
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: zinc[500],
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

/**
 * The editor for a field with repeating values: the shared checklist, with (blank), and a Contains box for free text.
 *
 * The checklist offers every value the rows carry (the whole registry for an enum, Yes/No for a boolean), and (blank)
 * for the rows with no value. The first box takes focus when the menu opens, so a click on the header and typing is
 * the whole gesture.
 */
function ValuesEditor({
  field,
  kind,
  rows,
  current,
  withContains,
  onApply,
}: {
  field: DispatchField;
  kind: FieldKind;
  rows: readonly DispatchTableRow[];
  current: FilterNode[];
  withContains: boolean;
  onApply: (next: FilterNode[]) => void;
}) {
  const options = useMemo(() => valueOptions(rows, field, kind), [rows, field, kind]);
  const blanks = useMemo(() => blankCount(rows, field), [rows, field]);
  const initial = useMemo(() => {
    const chosen = new Set(chosenValues(current, field));
    let contains = "";
    for (const condition of current) {
      if ("field" in condition && "regex" in condition) contains = unescapeRegExp(condition.regex.pattern);
    }
    return { chosen, blank: blankChosen(current, field), contains };
  }, [current, field]);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(initial.chosen);
  const [blank, setBlank] = useState(initial.blank);
  const [contains, setContains] = useState(initial.contains);
  // The (blank) choice is offered wherever the rows offer values, or carry blanks, or a condition already chose it.
  const offersBlank = rows.length > 0 || initial.blank;
  const apply = () => {
    const leaves = valuesCondition(field, [...chosen], blank);
    const term = contains.trim();
    if (term !== "") leaves.push({ field: fieldRefOf(field), regex: { pattern: escapeRegExp(term), flags: "i" } });
    onApply(leaves);
  };
  return (
    <Box>
      {withContains && (
        <InputBase
          autoFocus
          value={contains}
          onChange={(event) => setContains(event.target.value)}
          placeholder="Contains…"
          inputProps={{ "aria-label": "Contains" }}
          fullWidth
          sx={{ ...editorInputSx, mb: 0.75 }}
        />
      )}
      {/* With nothing to tick, a column that has the Contains box shows it alone, without the checklist's empty note. */}
      {(!withContains || options.length > 0 || offersBlank) && (
        <ValueChecklist
          options={options}
          chosen={chosen}
          onChosenChange={setChosen}
          multiple
          blank={offersBlank ? { count: blanks, checked: blank, onChange: setBlank } : undefined}
          autoFocus={!withContains}
        />
      )}
      <EditorActions
        canApply={chosen.size > 0 || blank || contains.trim() !== "" || current.length > 0}
        canClear={current.length > 0}
        onApply={apply}
        onClear={() => onApply([])}
      />
    </Box>
  );
}

type TimeUnit = "minutes" | "hours" | "days";
const MINUTES_PER_UNIT: Record<TimeUnit, number> = { minutes: 1, hours: 60, days: 1440 };

/** Picks the largest unit a span divides evenly into, so 120 minutes reads back as 2 hours; empty starts in hours. */
function splitMinutes(minutes: number): { amount: string; unit: TimeUnit } {
  if (minutes <= 0) return { amount: "", unit: "hours" };
  if (minutes % 1440 === 0) return { amount: String(minutes / 1440), unit: "days" };
  if (minutes % 60 === 0) return { amount: String(minutes / 60), unit: "hours" };
  return { amount: String(minutes), unit: "minutes" };
}

/** The editor for a timestamp: within the last N, or older than N, in minutes, hours, or days. */
function TimeEditor({
  field,
  current,
  onApply,
}: {
  field: DispatchField;
  current: FilterNode[];
  onApply: (next: FilterNode[]) => void;
}) {
  const leaf = positiveLeaf(current);
  const initialMode = leaf && "olderThan" in leaf ? "olderThan" : "newerThan";
  const initialMinutes =
    leaf && "olderThan" in leaf ? leaf.olderThan.minutes : leaf && "newerThan" in leaf ? leaf.newerThan.minutes : 0;
  const [mode, setMode] = useState<"newerThan" | "olderThan">(initialMode);
  const [{ amount, unit }, setSpan] = useState(() => splitMinutes(initialMinutes));
  const minutes = Number(amount) * MINUTES_PER_UNIT[unit];
  const valid = amount.trim() !== "" && Number.isFinite(minutes) && minutes > 0;
  return (
    <Box>
      <SegmentedControl
        testId={TestIds.dispatch.columnFilterOption}
        options={[
          { label: "Within last", value: "newerThan" as const },
          { label: "Older than", value: "olderThan" as const },
        ]}
        value={mode}
        onChange={(next) => setMode(next)}
      />
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <InputBase
          autoFocus
          type="number"
          value={amount}
          onChange={(event) => setSpan({ amount: event.target.value, unit })}
          placeholder="Amount"
          inputProps={{ "aria-label": "Amount", min: 1 }}
          sx={{ ...editorInputSx, flex: 1 }}
        />
        <Select
          value={unit}
          onChange={(event) => setSpan({ amount, unit: event.target.value as TimeUnit })}
          inputProps={{ "aria-label": "Unit" }}
          MenuProps={{ slotProps: menuSlotProps }}
          sx={{ ...selectSx, width: 110 }}
        >
          <MenuItem value="minutes" sx={{ fontSize: "0.8125rem" }}>
            minutes
          </MenuItem>
          <MenuItem value="hours" sx={{ fontSize: "0.8125rem" }}>
            hours
          </MenuItem>
          <MenuItem value="days" sx={{ fontSize: "0.8125rem" }}>
            days
          </MenuItem>
        </Select>
      </Box>
      <EditorActions
        canApply={valid}
        canClear={current.length > 0}
        onApply={() =>
          onApply([
            mode === "olderThan"
              ? { field: fieldRefOf(field), olderThan: { minutes } }
              : { field: fieldRefOf(field), newerThan: { minutes } },
          ])
        }
        onClear={() => onApply([])}
      />
    </Box>
  );
}

/** The editor for a number: greater than, or less than, a comparand. */
function NumberEditor({
  field,
  current,
  onApply,
}: {
  field: DispatchField;
  current: FilterNode[];
  onApply: (next: FilterNode[]) => void;
}) {
  const leaf = positiveLeaf(current);
  const [mode, setMode] = useState<"gt" | "lt">(leaf && "lt" in leaf ? "lt" : "gt");
  const [amount, setAmount] = useState(() =>
    leaf && "gt" in leaf ? String(leaf.gt) : leaf && "lt" in leaf ? String(leaf.lt) : "",
  );
  const comparand = Number(amount);
  const valid = amount.trim() !== "" && Number.isFinite(comparand);
  return (
    <Box>
      <SegmentedControl
        testId={TestIds.dispatch.columnFilterOption}
        options={[
          { label: "Greater than", value: "gt" as const },
          { label: "Less than", value: "lt" as const },
        ]}
        value={mode}
        onChange={(next) => setMode(next)}
      />
      <InputBase
        autoFocus
        type="number"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        placeholder="Amount"
        inputProps={{ "aria-label": "Amount" }}
        fullWidth
        sx={editorInputSx}
      />
      <EditorActions
        canApply={valid}
        canClear={current.length > 0}
        onApply={() =>
          onApply([
            mode === "gt" ? { field: fieldRefOf(field), gt: comparand } : { field: fieldRefOf(field), lt: comparand },
          ])
        }
        onClear={() => onApply([])}
      />
    </Box>
  );
}

/**
 * The editor for a location: a center from an address or typed coordinates, a radius, and its unit, the same form the
 * driver list's location filter uses. Applies as a radius condition on the driver's coordinates.
 */
function GeoEditor({
  field,
  current,
  onApply,
}: {
  field: DispatchField;
  current: FilterNode[];
  onApply: (next: FilterNode[]) => void;
}) {
  const leaf = positiveLeaf(current);
  const spec: RadiusSpec | null = leaf && "withinRadius" in leaf ? leaf.withinRadius : null;
  const location: LocationFilter | null = spec
    ? { latitude: spec.latitude, longitude: spec.longitude, radius: spec.radius, unit: spec.unit }
    : null;
  // The address the center came from is form state below; a ref lets the apply callback, defined first, read it.
  const labelRef = useRef<string | null>(spec?.label ?? null);
  const form = useLocationFilterForm({
    location,
    setLocation: (next) =>
      onApply([
        {
          field: fieldRefOf(field),
          withinRadius: { ...next, ...(labelRef.current ? { label: labelRef.current } : {}) },
        },
      ]),
    clearLocation: () => onApply([]),
  });
  labelRef.current = form.searchType === "address" ? (form.addressLabel ?? spec?.label ?? null) : null;
  return (
    <Box>
      <SegmentedControl
        testId={TestIds.dispatch.columnFilterOption}
        options={[
          { label: "Address", value: "address" as const },
          { label: "Coordinates", value: "coordinates" as const },
        ]}
        value={form.searchType}
        onChange={(next) => form.setSearchType(next)}
      />
      {form.searchType === "address" ? (
        <Box sx={{ mb: 1 }}>
          <GooglePlacesAutocomplete
            location={form.addressLabel ?? spec?.label ?? null}
            resetForm={form.resetForm}
            setLocationValue={form.handlePlaceSelected}
          />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 0.75, mb: 1 }}>
          <InputBase
            value={form.latitude}
            onChange={(event) => form.setLatitude(event.target.value)}
            placeholder="Latitude"
            inputProps={{ "aria-label": "Latitude" }}
            sx={{ ...editorInputSx, flex: 1 }}
          />
          <InputBase
            value={form.longitude}
            onChange={(event) => form.setLongitude(event.target.value)}
            placeholder="Longitude"
            inputProps={{ "aria-label": "Longitude" }}
            sx={{ ...editorInputSx, flex: 1 }}
          />
        </Box>
      )}
      <Typography sx={{ fontSize: "0.75rem", color: zinc[400], px: 0.5 }}>
        Within {form.range} {form.distanceUnit}
        {form.isValid ? ` of ${form.latNum.toFixed(4)}, ${form.lonNum.toFixed(4)}` : ""}
      </Typography>
      <Box sx={{ px: 1 }}>
        <Slider
          min={1}
          max={500}
          size="small"
          value={form.range}
          onChange={(_event, value) => form.setRange(value as number)}
          aria-label="Radius"
          sx={{
            color: "var(--color-brand-primary-500)",
            "& .MuiSlider-thumb": { color: "white", width: 12, height: 12 },
            "& .MuiSlider-rail": { color: zinc[500], opacity: 0.3 },
          }}
        />
      </Box>
      <SegmentedControl
        testId={TestIds.dispatch.columnFilterOption}
        options={[
          { label: "Miles", value: "miles" as const },
          { label: "Kilometers", value: "km" as const },
        ]}
        value={form.distanceUnit}
        onChange={(next) => form.setDistanceUnit(next)}
      />
      <EditorActions canApply={form.isValid} canClear={current.length > 0} onApply={form.apply} onClear={form.reset} />
    </Box>
  );
}

const itemSx = { display: "flex", gap: 1, alignItems: "center", fontSize: "0.8125rem", minHeight: 32 } as const;

/**
 * The popover a click on a column header opens, anchored to the header: sort ascending or descending by the column,
 * an editor that filters by what the column is (values to tick for enums, booleans, tags, and names; a contained
 * term for message text; a span for a time; a comparison for a number; a center and radius for the location), then
 * pin and hide. Applying replaces whatever conditions the column already carried in the strip.
 */
export default function DispatchColumnMenu({
  target,
  rows,
  adhoc,
  sort,
  pinned,
  canHide,
  onClose,
  onSort,
  onReplaceConditions,
  onTogglePin,
  onHide,
}: {
  target: ColumnMenuTarget | null;
  /** Every row before filtering, so the values offered are the whole vocabulary rather than what survived. */
  rows: readonly DispatchTableRow[];
  adhoc: readonly FilterNode[];
  sort: { key: string; direction: "asc" | "desc" } | null;
  pinned: boolean;
  canHide: boolean;
  onClose: () => void;
  onSort: (columnId: string, direction: "asc" | "desc") => void;
  onReplaceConditions: (field: DispatchField, next: FilterNode[]) => void;
  onTogglePin: (columnId: string) => void;
  onHide: (columnId: string) => void;
}) {
  if (!target) return null;
  const { field: targetField, editor } = editorFor(target.columnId, target.kind);
  const current = conditionsOnField(adhoc, targetField);
  const sortedHere = sort?.key === target.columnId ? sort.direction : null;
  const apply = (next: FilterNode[]) => {
    onReplaceConditions(targetField, next);
    onClose();
  };
  const sortItem = (direction: "asc" | "desc") => (
    <MenuItem
      selected={sortedHere === direction}
      onClick={() => {
        onSort(target.columnId, direction);
        onClose();
      }}
      sx={itemSx}
    >
      {direction === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
      {direction === "asc" ? "Sort ascending" : "Sort descending"}
    </MenuItem>
  );
  // Remounting the editor per column keeps each one's draft its own.
  const editorKey = `${target.columnId}:${current.length}`;
  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          "data-sb": TestIds.dispatch.columnMenu,
          sx: {
            width: editor === "geo" ? 360 : 280,
            bgcolor: zinc[800],
            color: "white",
            border: `1px solid ${zinc[600]}`,
            mt: 0.25,
            p: 0.5,
          },
        } as React.ComponentProps<typeof Popover>["PaperProps"],
      }}
    >
      <Typography sx={{ px: 1, py: 0.5, fontSize: "0.8125rem", fontWeight: 600 }}>{target.label}</Typography>
      {sortItem("asc")}
      {sortItem("desc")}
      {editor !== "none" && (
        <>
          <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />
          <Typography sx={sectionLabelSx}>Filter</Typography>
          <Box sx={{ px: 0.5, pb: 0.5 }}>
            {editor === "values" || editor === "contains" ? (
              <ValuesEditor
                key={editorKey}
                field={targetField}
                kind={target.kind}
                rows={editor === "contains" ? [] : rows}
                current={current}
                withContains={editor === "contains" || target.kind.valueType === "text"}
                onApply={apply}
              />
            ) : editor === "time" ? (
              <TimeEditor key={editorKey} field={targetField} current={current} onApply={apply} />
            ) : editor === "number" ? (
              <NumberEditor key={editorKey} field={targetField} current={current} onApply={apply} />
            ) : (
              <GeoEditor key={editorKey} field={targetField} current={current} onApply={apply} />
            )}
          </Box>
        </>
      )}
      <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />
      <MenuItem
        onClick={() => {
          onTogglePin(target.columnId);
          onClose();
        }}
        sx={itemSx}
      >
        {pinned ? <PushPinOutlinedIcon sx={{ fontSize: 16 }} /> : <PushPinIcon sx={{ fontSize: 16 }} />}
        {pinned ? "Unpin" : "Pin to left"}
      </MenuItem>
      <MenuItem
        disabled={!canHide}
        onClick={() => {
          onHide(target.columnId);
          onClose();
        }}
        sx={itemSx}
      >
        <VisibilityOffIcon sx={{ fontSize: 16 }} />
        Hide column
      </MenuItem>
    </Popover>
  );
}
